import React, { useState } from "react";
import styled from "styled-components";
import { Schedule, Repeat, ViewDay, AccessTime, Add, Delete } from "@material-ui/icons";

const TYPES = [
  { key: "material",  label: "Material",        description: "Sincronização de materiais" },
  { key: "stockTransfer", label: "Stock Transfer", description: "Sincronização da transferência de estoque" },
  { key: "stock",     label: "Stock",            description: "Sincronização de estoque" },
];

const INTERVAL_PRESETS = [
  { value: 1,   label: "1 min" },
  { value: 2,   label: "2 min" },
  { value: 5,   label: "5 min" },
  { value: 10,  label: "10 min" },
  { value: 15,  label: "15 min" },
  { value: 30,  label: "30 min" },
  { value: 60,  label: "1 hora" },
  { value: 120, label: "2 horas" },
  { value: 240, label: "4 horas" },
  { value: 480, label: "8 horas" },
  { value: 720, label: "12 horas" },
];

const TIMES_PER_DAY_PRESETS = [
  { value: 1,  label: "1x ao dia" },
  { value: 2,  label: "2x ao dia" },
  { value: 3,  label: "3x ao dia" },
  { value: 4,  label: "4x ao dia" },
  { value: 6,  label: "6x ao dia" },
  { value: 8,  label: "8x ao dia" },
  { value: 12, label: "12x ao dia" },
  { value: 24, label: "24x ao dia" },
];

const HOURS   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));

const DEFAULT_CONFIG = { enabled: false, mode: "interval", intervalMinutes: 30, timesPerDay: 2, times: [] };

function intervalLabel(minutes) {
  if (!minutes) return "—";
  if (minutes < 60) return `a cada ${minutes} min`;
  const h = minutes / 60;
  return `a cada ${h} hora${h !== 1 ? "s" : ""}`;
}

function timesLabel(n) {
  const intervalMin = Math.round(1440 / n);
  return `${n}x ao dia (~${intervalLabel(intervalMin)})`;
}

function SummaryBadge({ config }) {
  const { mode, intervalMinutes, timesPerDay, times } = config;
  if (mode === "interval")    return <Summary><Repeat style={{ fontSize: "14px", marginRight: "6px" }} />{intervalLabel(intervalMinutes || 30)}</Summary>;
  if (mode === "timesPerDay") return <Summary><ViewDay style={{ fontSize: "14px", marginRight: "6px" }} />{timesLabel(timesPerDay || 2)}</Summary>;
  return <Summary><AccessTime style={{ fontSize: "14px", marginRight: "6px" }} />{(times || []).length} horário{(times || []).length !== 1 ? "s" : ""}</Summary>;
}

function SchedulerSection({ label, description, config, onChange }) {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const [hour,   setHour]   = useState("08");
  const [minute, setMinute] = useState("00");

  const toggleEnabled  = ()     => onChange({ ...cfg, enabled: !cfg.enabled });
  const setMode        = (mode) => onChange({ ...cfg, mode });
  const setInterval    = (v)    => onChange({ ...cfg, intervalMinutes: Number(v) });
  const setTimesPerDay = (v)    => onChange({ ...cfg, timesPerDay: Number(v) });

  const addTime = () => {
    const time = `${hour}:${minute}`;
    const times = cfg.times || [];
    if (times.includes(time)) return;
    onChange({ ...cfg, times: [...times, time].sort() });
  };

  const removeTime = (time) => {
    onChange({ ...cfg, times: (cfg.times || []).filter((t) => t !== time) });
  };

  return (
    <Section enabled={cfg.enabled}>
      <SectionHeader>
        <SectionIcon enabled={cfg.enabled}>
          <Schedule style={{ fontSize: "20px", color: cfg.enabled ? "#ff6b35" : "#94a3b8" }} />
        </SectionIcon>
        <SectionInfo>
          <SectionTitle>{label}</SectionTitle>
          <SectionDesc>{description}</SectionDesc>
        </SectionInfo>
        {cfg.enabled && <SummaryBadge config={cfg} />}
        <ToggleWrapper onClick={toggleEnabled} enabled={cfg.enabled}>
          <ToggleTrack enabled={cfg.enabled}>
            <ToggleThumb enabled={cfg.enabled} />
          </ToggleTrack>
          <ToggleLabel enabled={cfg.enabled}>
            {cfg.enabled ? "Ativo" : "Inativo"}
          </ToggleLabel>
        </ToggleWrapper>
      </SectionHeader>

      <SectionBody disabled={!cfg.enabled}>
        {/* Mode toggle */}
        <ModeRow>
          <ModeBtn active={cfg.mode === "interval"}    type="button" onClick={() => setMode("interval")}>
            <Repeat style={{ fontSize: "15px", marginRight: "5px" }} />
            Intervalo
          </ModeBtn>
          <ModeBtn active={cfg.mode === "timesPerDay"} type="button" onClick={() => setMode("timesPerDay")}>
            <ViewDay style={{ fontSize: "15px", marginRight: "5px" }} />
            Vezes ao dia
          </ModeBtn>
          <ModeBtn active={cfg.mode === "fixedTimes"}  type="button" onClick={() => setMode("fixedTimes")}>
            <AccessTime style={{ fontSize: "15px", marginRight: "5px" }} />
            Horário fixo
          </ModeBtn>
        </ModeRow>

        {/* Interval mode */}
        {cfg.mode === "interval" && (
          <ConfigBlock>
            <ConfigLabel>Executar a cada:</ConfigLabel>
            <PresetGrid>
              {INTERVAL_PRESETS.map(({ value, label: lbl }) => (
                <PresetChip key={value} type="button" active={cfg.intervalMinutes === value} onClick={() => setInterval(value)}>
                  {lbl}
                </PresetChip>
              ))}
            </PresetGrid>
            <CustomRow>
              <ConfigLabel style={{ marginBottom: 0 }}>Ou informe em minutos:</ConfigLabel>
              <CustomInput type="number" min="1" max="1440" value={cfg.intervalMinutes || ""} onChange={(e) => setInterval(e.target.value)} />
              <UnitLabel>min</UnitLabel>
            </CustomRow>
            <InfoText>
              <AccessTime style={{ fontSize: "13px", marginRight: "4px" }} />
              Executa <strong>{intervalLabel(cfg.intervalMinutes || 30)}</strong>
            </InfoText>
          </ConfigBlock>
        )}

        {/* Times per day mode */}
        {cfg.mode === "timesPerDay" && (
          <ConfigBlock>
            <ConfigLabel>Quantas vezes ao dia:</ConfigLabel>
            <PresetGrid>
              {TIMES_PER_DAY_PRESETS.map(({ value, label: lbl }) => (
                <PresetChip key={value} type="button" active={cfg.timesPerDay === value} onClick={() => setTimesPerDay(value)}>
                  {lbl}
                </PresetChip>
              ))}
            </PresetGrid>
            <CustomRow>
              <ConfigLabel style={{ marginBottom: 0 }}>Ou informe a quantidade:</ConfigLabel>
              <CustomInput type="number" min="1" max="1440" value={cfg.timesPerDay || ""} onChange={(e) => setTimesPerDay(e.target.value)} />
              <UnitLabel>x/dia</UnitLabel>
            </CustomRow>
            <InfoText>
              <AccessTime style={{ fontSize: "13px", marginRight: "4px" }} />
              {timesLabel(cfg.timesPerDay || 2)} — o scheduler distribui igualmente ao longo do dia
            </InfoText>
          </ConfigBlock>
        )}

        {/* Fixed times mode */}
        {cfg.mode === "fixedTimes" && (
          <ConfigBlock>
            <ConfigLabel>Horários configurados:</ConfigLabel>
            {(cfg.times || []).length > 0 ? (
              <SlotList>
                {(cfg.times || []).map((time) => (
                  <SlotChip key={time}>
                    <AccessTime style={{ fontSize: "13px", marginRight: "5px", color: "#ff6b35" }} />
                    {time}
                    <RemoveBtn type="button" onClick={() => removeTime(time)} title="Remover">
                      <Delete style={{ fontSize: "13px" }} />
                    </RemoveBtn>
                  </SlotChip>
                ))}
              </SlotList>
            ) : (
              <EmptySlots>Nenhum horário configurado. Adicione abaixo.</EmptySlots>
            )}
            <AddRow>
              <TimeSelect value={hour} onChange={(e) => setHour(e.target.value)}>
                {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
              </TimeSelect>
              <TimeSep>:</TimeSep>
              <TimeSelect value={minute} onChange={(e) => setMinute(e.target.value)}>
                {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
              </TimeSelect>
              <AddButton type="button" onClick={addTime}>
                <Add style={{ fontSize: "17px", marginRight: "5px" }} />
                Adicionar
              </AddButton>
            </AddRow>
            {(cfg.times || []).length > 0 && (
              <InfoText>
                <AccessTime style={{ fontSize: "13px", marginRight: "4px" }} />
                Executa às <strong>{(cfg.times || []).join(", ")}</strong>
              </InfoText>
            )}
          </ConfigBlock>
        )}
      </SectionBody>
    </Section>
  );
}

function TabScheduler({ values, setFieldValue }) {
  const scheduler = values.scheduler || {};

  const handleChange = (typeKey, config) => {
    setFieldValue("scheduler", { ...scheduler, [typeKey]: config });
  };

  return (
    <Wrapper>
      <PageHeader>
        <HeaderIcon>
          <Schedule style={{ fontSize: "24px", color: "#ff6b35" }} />
        </HeaderIcon>
        <HeaderText>
          <HeaderTitle>Agendamento de Sincronização</HeaderTitle>
          <HeaderSub>
            Configure a frequência de execução para cada tipo. A configuração é salva no DynamoDB
            e consumida pelo scheduler Node.js no ECS/Docker.
          </HeaderSub>
        </HeaderText>
      </PageHeader>

      {TYPES.map(({ key, label, description }) => (
        <SchedulerSection
          key={key}
          typeKey={key}
          label={label}
          description={description}
          config={scheduler[key] || {}}
          onChange={(cfg) => handleChange(key, cfg)}
        />
      ))}

      <ConfigPreview>
        <PreviewTitle>Configuração DynamoDB (prévia)</PreviewTitle>
        <PreviewCode>{JSON.stringify({ scheduler }, null, 2)}</PreviewCode>
      </ConfigPreview>
    </Wrapper>
  );
}

export default TabScheduler;

/* ─── Styled Components ──────────────────────────────────────────────────── */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: 'Mulish', sans-serif;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: rgba(255, 107, 53, 0.04);
  border: 1px solid rgba(255, 107, 53, 0.15);
  border-radius: 12px;
`;

const HeaderIcon = styled.div`
  width: 44px;
  height: 44px;
  background: rgba(255, 107, 53, 0.1);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const HeaderText = styled.div`flex: 1;`;

const HeaderTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 4px 0;
`;

const HeaderSub = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
`;

const Section = styled.div`
  border: 1px solid ${(p) => p.enabled ? "rgba(255,107,53,0.3)" : "#e2e8f0"};
  border-radius: 12px;
  overflow: hidden;
  background: white;
  transition: all 0.2s ease;
  opacity: ${(p) => p.enabled ? 1 : 0.75};

  &:hover {
    box-shadow: 0 2px 12px rgba(255, 107, 53, 0.08);
    border-color: rgba(255, 107, 53, 0.3);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(255, 107, 53, 0.04) 0%, rgba(255, 107, 53, 0.01) 100%);
  border-bottom: 1px solid #e2e8f0;
`;

const SectionIcon = styled.div`
  width: 36px;
  height: 36px;
  background: ${(p) => p.enabled ? "rgba(255,107,53,0.1)" : "rgba(148,163,184,0.1)"};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s;
`;

const SectionInfo = styled.div`flex: 1;`;

const SectionTitle = styled.h4`
  font-size: 15px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 2px 0;
`;

const SectionDesc = styled.p`
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
`;

const Summary = styled.span`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  color: #ff6b35;
  background: rgba(255, 107, 53, 0.1);
  padding: 5px 12px;
  border-radius: 20px;
  white-space: nowrap;
`;

const SectionBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  pointer-events: ${(p) => p.disabled ? "none" : "auto"};
  opacity: ${(p) => p.disabled ? 0.4 : 1};
  transition: opacity 0.2s;
`;

const ModeRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ModeBtn = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Mulish', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid ${(p) => (p.active ? "#ff6b35" : "#e2e8f0")};
  background: ${(p) => (p.active ? "rgba(255,107,53,0.08)" : "white")};
  color: ${(p) => (p.active ? "#ff6b35" : "#64748b")};

  &:hover {
    border-color: #ff6b35;
    color: #ff6b35;
  }
`;

const ConfigBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
`;

const ConfigLabel = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin: 0 0 4px 0;
`;

const PresetGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const PresetChip = styled.button`
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'Mulish', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1.5px solid ${(p) => (p.active ? "#ff6b35" : "#cbd5e1")};
  background: ${(p) => (p.active ? "linear-gradient(135deg, #ff6b35 0%, #e85a2a 100%)" : "white")};
  color: ${(p) => (p.active ? "white" : "#64748b")};
  box-shadow: ${(p) => (p.active ? "0 2px 6px rgba(255,107,53,0.3)" : "none")};

  &:hover {
    border-color: #ff6b35;
    color: ${(p) => (p.active ? "white" : "#ff6b35")};
  }
`;

const CustomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CustomInput = styled.input`
  width: 80px;
  padding: 7px 10px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Mulish', sans-serif;
  color: #2c3e50;
  outline: none;
  text-align: center;

  &:focus {
    border-color: #ff6b35;
    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.1);
  }
`;

const UnitLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
`;

const InfoText = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #64748b;
  padding: 8px 12px;
  background: rgba(255, 107, 53, 0.04);
  border-radius: 8px;
  border-left: 3px solid #ff6b35;

  strong {
    color: #ff6b35;
    margin: 0 3px;
  }
`;

const SlotList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SlotChip = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 10px 6px 12px;
  background: rgba(255, 107, 53, 0.06);
  border: 1px solid rgba(255, 107, 53, 0.2);
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  font-family: 'Mulish', sans-serif;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  padding: 0 0 0 6px;
  cursor: pointer;
  color: #e74c3c;
  display: flex;
  align-items: center;
  opacity: 0.6;
  transition: opacity 0.2s;

  &:hover { opacity: 1; }
`;

const EmptySlots = styled.p`
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
  font-style: italic;
`;

const AddRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
`;

const TimeSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Mulish', sans-serif;
  font-weight: 600;
  color: #2c3e50;
  background: white;
  cursor: pointer;
  outline: none;
  min-width: 70px;

  &:focus {
    border-color: #ff6b35;
    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.1);
  }
`;

const TimeSep = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: #64748b;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background: linear-gradient(135deg, #ff6b35 0%, #e85a2a 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Mulish', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(255, 107, 53, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(255, 107, 53, 0.4);
  }

  &:active { transform: translateY(0); }
`;

const ConfigPreview = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
`;

const PreviewTitle = styled.div`
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
`;

const PreviewCode = styled.pre`
  margin: 0;
  padding: 16px;
  font-size: 12px;
  color: #475569;
  background: #f8fafc;
  overflow-x: auto;
  font-family: 'Courier New', monospace;
  line-height: 1.6;
`;

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const ToggleTrack = styled.div`
  width: 42px;
  height: 24px;
  border-radius: 12px;
  background: ${(p) => p.enabled ? "linear-gradient(135deg, #ff6b35, #e85a2a)" : "#cbd5e1"};
  position: relative;
  transition: background 0.25s ease;
  flex-shrink: 0;
  box-shadow: ${(p) => p.enabled ? "0 2px 6px rgba(255,107,53,0.35)" : "none"};
`;

const ToggleThumb = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  position: absolute;
  top: 3px;
  left: ${(p) => p.enabled ? "21px" : "3px"};
  transition: left 0.25s ease;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
`;

const ToggleLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${(p) => p.enabled ? "#ff6b35" : "#94a3b8"};
  font-family: 'Mulish', sans-serif;
  transition: color 0.2s;
  min-width: 46px;
`;
